import * as React from 'react';
import {format} from 'date-fns';
import {LegacyModal} from '@flexe/ui-components';
import {ReactFragment, useEffect, useState} from 'react';
import EditableValueDisplay from '../../shared/EditableValueDisplay';
import {Destination} from '../outbound-shipments/ShipmentInterfaces';
import {OutboundTrailerManifest} from './ManifestInterfaces';

export interface CompleteManifestModalProps {
  authenticityToken: string;
  showModal: boolean;
  toggleModal: () => void;
  onComplete: () => void;
  manifest: OutboundTrailerManifest;
  scheduledShipDate: string;
  scac: string;
  proNumber: string;
  trailerNumber: string;
  sealNumber: string;
  destinationId: string;
  destination: Destination;
  palletsLoaded: number;

  setScac: (value: string) => void;
  saveScac: () => void;
  setProNumber: (value: string) => void;
  saveProNumber: () => void;
  setTrailerNumber: (value: string) => void;
  saveTrailerNumber: () => void;
  setSealNumber: (value: string) => void;
  saveSealNumber: () => void;
}

export const CompleteManifestModal: React.FC<CompleteManifestModalProps> = (props) => {
  useEffect(() => {
    checkCompleteEnabled();
  }, [props.scac, props.trailerNumber, props.proNumber, props.sealNumber, props.palletsLoaded]);

  const canComplete = (): boolean => {
    return props.scac && props.trailerNumber && props.proNumber && props.sealNumber && props.palletsLoaded > 0;
  };
  const [isCompleteEnabled, setIsCompleteEnabled] = useState<boolean>(false);
  const requiredMessage = '*Carrier(SCAC), PRO #, Trailer #, and Seal # are required to complete manifest';
  const title = (
    <div>
      <span className="title">{`Complete Manifest #${props.manifest.id}`}</span>
    </div>
  );
  const statusDot = <span className={`dot ${'in-progress'}`} />;
  const lineInfo = (label: string, value: any, leftFragment?: ReactFragment): ReactFragment => {
    const valueText = value ? value.toString() : '-';
    return (
      <React.Fragment>
        <div className="modal-row">
          <div className="left-column">{label}</div>
          <div className={'right-column' + (value ? '' : ' missing-value')}>
            {leftFragment}
            {' ' + valueText}
          </div>
        </div>
      </React.Fragment>
    );
  };

  const editableLineInfo = (
    label: string,
    value: any,
    ghostText: string,
    inputType: string,
    onChange: (val: any) => void,
    onSave?: () => void,
    errorMessage?: string,
    maxLength: number = Number.MAX_SAFE_INTEGER
  ): ReactFragment => {
    return (
      <React.Fragment>
        <div className="modal-row">
          <div className="left-column">{label}</div>
          <div className="right-column">
            <EditableValueDisplay
              value={value}
              ghostText={ghostText}
              startEnabled={false}
              inputType={inputType}
              maxLength={maxLength}
              errorMessage={errorMessage}
              onChange={onChange}
              onSave={onSave}
            />
          </div>
        </div>
      </React.Fragment>
    );
  };

  const checkCompleteEnabled = () => {
    setIsCompleteEnabled(canComplete());
  };

  const scheduledShipDate = () => {
    return props.scheduledShipDate ? format(new Date(props.scheduledShipDate), 'MMMM Do YYYY') : '';
  };

  const destinationString = () => {
    const dest = props.destination;
    if (!dest) {
      return '';
    }
    const addressLines = [dest.address_line_1, dest.address_line_2, dest.address_line_3].filter(Boolean).join(' ');
    return `${addressLines} ${dest.city}, ${dest.state} ${dest.postal_code}, ${dest.country}`;
  };

  return (
    <LegacyModal
      id="complete_manifest_modal"
      show={props.showModal}
      size="small"
      title={title}
      toggleModal={props.toggleModal}
    >
      <div id="complete-manifest-modal-fields">
        {lineInfo('Status', 'In progress', statusDot)}
        {lineInfo('Location', props.manifest.doorLocation)}
        {lineInfo('Scheduled Ship Date', scheduledShipDate())}
        {
          <React.Fragment>
            <div className="required">
              {' '}
              {editableLineInfo('Carrier(SCAC)', props.scac, 'Add SCAC', 'text', props.setScac, props.saveScac)}
            </div>
            <div className="required">
              {editableLineInfo('PRO #', props.proNumber, 'Add PRO #', 'text', props.setProNumber, props.saveProNumber)}
            </div>
            <div className="required">
              {editableLineInfo(
                'Trailer #',
                props.trailerNumber,
                'Add Trailer #',
                'text',
                props.setTrailerNumber,
                props.saveTrailerNumber
              )}
            </div>
            <div className="required">
              {editableLineInfo(
                'Seal #',
                props.sealNumber,
                'Add Seal #',
                'text',
                props.setSealNumber,
                props.saveSealNumber
              )}
            </div>
          </React.Fragment>
        }
        {lineInfo('Destination ID', props?.destinationId)}
        {lineInfo('Destination', destinationString())}
        {lineInfo('Pallets Loaded', props.palletsLoaded)}
        <button className="btn" disabled={!isCompleteEnabled} onClick={props.onComplete}>
          Ship
        </button>
        <div className="subtext">{requiredMessage}</div>
      </div>
    </LegacyModal>
  );
};

export default CompleteManifestModal;
